import {
  COLLAB_RECONNECT_BASE_DELAY_MS,
  COLLAB_RECONNECT_MAX_ATTEMPTS,
  COLLAB_RECONNECT_MAX_DELAY_MS,
} from "../app_constants";

import {
  parseRoomServerEnvelope,
  ROOM_CONTROL_CODES,
  type ClientRelayEnvelopeV1,
  type RelayDurabilityV1,
  type RelayEnvelopeV1,
  type RoomControlEnvelopeV1,
} from "./roomProtocol";

// Mirrors `SESSION_EXPIRED_CLOSE_CODE` in `workers/collab/src/room-do.ts`.
// The alarm sends a `room_closing` control message, then closes every socket
// with this code; a client that reconnected here would just be told to
// close again, so this is treated as terminal, not transient.
const SESSION_EXPIRED_CLOSE_CODE = 4000;

// WebSocket readyState values, per the WHATWG spec. Read from a local
// constant instead of the global `WebSocket.OPEN` so this class does not
// depend on the injected constructor exposing static properties.
const READY_STATE_OPEN = 1;

export interface RoomTransportEvents {
  /** Fires once the WebSocket handshake completes, including on reconnect. */
  open: () => void;
  control: (control: RoomControlEnvelopeV1) => void;
  relay: (envelope: RelayEnvelopeV1) => void;
  /** Fires before each bounded backoff attempt. */
  reconnecting: (info: { attempt: number; delayMs: number }) => void;
  /** Fires once the bounded number of reconnect attempts is exhausted. */
  reconnectFailed: () => void;
}

type EventName = keyof RoomTransportEvents;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyHandler = (...args: any[]) => void;

export interface RoomTransportOptions {
  /** Injectable WebSocket constructor, so tests can supply a fake implementation. */
  webSocketCtor?: new (url: string) => WebSocket;
  reconnectBaseDelayMs?: number;
  reconnectMaxDelayMs?: number;
  reconnectMaxAttempts?: number;
}

/**
 * Wraps one raw WebSocket connection to a `RoomDO` room. Exists because raw
 * WebSocket, unlike socket.io, has no built-in reconnection: this class owns
 * that state machine (bounded exponential backoff, terminal handling of
 * close code 4000) plus a small multi-subscriber event API so `Portal` and
 * `Collab` can each listen for what they care about, mirroring how they used
 * to attach independent `socket.on(...)` listeners to the same socket.
 */
export class RoomTransport {
  /** Set once the first `member_list` control message assigns our identity. */
  clientId: string | null = null;

  private socket: WebSocket | null = null;
  private readonly listeners = new Map<EventName, Set<AnyHandler>>();
  private readonly webSocketCtor: new (url: string) => WebSocket;
  private readonly reconnectBaseDelayMs: number;
  private readonly reconnectMaxDelayMs: number;
  private readonly reconnectMaxAttempts: number;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manuallyClosed = false;
  private terminal = false;

  /** Stores the room's WebSocket URL and reconnect tuning for one collaboration session. */
  constructor(
    private readonly url: string,
    options: RoomTransportOptions = {},
  ) {
    this.webSocketCtor = options.webSocketCtor ?? WebSocket;
    this.reconnectBaseDelayMs =
      options.reconnectBaseDelayMs ?? COLLAB_RECONNECT_BASE_DELAY_MS;
    this.reconnectMaxDelayMs =
      options.reconnectMaxDelayMs ?? COLLAB_RECONNECT_MAX_DELAY_MS;
    this.reconnectMaxAttempts =
      options.reconnectMaxAttempts ?? COLLAB_RECONNECT_MAX_ATTEMPTS;
  }

  /** True once the underlying WebSocket has completed its handshake. */
  get isConnected(): boolean {
    return this.socket?.readyState === READY_STATE_OPEN;
  }

  /** True once close code 4000 (session expired) has been received. No further reconnects follow. */
  get isTerminal(): boolean {
    return this.terminal;
  }

  /** Returns bytes queued by the browser but not yet written to the network. */
  get bufferedAmount(): number {
    return this.socket?.bufferedAmount ?? 0;
  }

  /** Subscribes one handler to one transport event. Safe to call more than once per event. */
  on<K extends EventName>(event: K, handler: RoomTransportEvents[K]): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler as AnyHandler);
  }

  /** Removes one previously subscribed handler. */
  off<K extends EventName>(event: K, handler: RoomTransportEvents[K]): void {
    this.listeners.get(event)?.delete(handler as AnyHandler);
  }

  private emit<K extends EventName>(
    event: K,
    ...args: Parameters<RoomTransportEvents[K]>
  ): void {
    for (const handler of this.listeners.get(event) ?? []) {
      handler(...args);
    }
  }

  /** Opens the WebSocket. Reconnect attempts after an unexpected close are scheduled internally. */
  connect(): void {
    this.manuallyClosed = false;
    this.openSocket();
  }

  private openSocket(): void {
    const socket = new this.webSocketCtor(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.emit("open");
    };

    socket.onmessage = (event: MessageEvent) => {
      // The relay protocol only ever sends text frames; anything else is not
      // something this client understands, so it is dropped rather than
      // trusted.
      if (typeof event.data !== "string") {
        return;
      }
      const envelope = parseRoomServerEnvelope(event.data);
      if (envelope === null) {
        console.error("Ignoring malformed room relay message");
        return;
      }
      if (envelope.kind === "control") {
        if (envelope.code === ROOM_CONTROL_CODES.MEMBER_LIST) {
          this.clientId = envelope.selfClientId;
        }
        this.emit("control", envelope);
      } else {
        this.emit("relay", envelope);
      }
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      if (event.code === SESSION_EXPIRED_CLOSE_CODE) {
        this.terminal = true;
      }
      if (this.manuallyClosed || this.terminal) {
        return;
      }
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      // The close event that follows carries the actionable code/reason;
      // this handler exists only so a failure is never silently dropped.
      console.error(`RoomTransport WebSocket error (attempt ${this.attempt})`);
    };
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    if (this.attempt > this.reconnectMaxAttempts) {
      this.emit("reconnectFailed");
      return;
    }
    const delayMs = Math.min(
      this.reconnectBaseDelayMs * 2 ** (this.attempt - 1),
      this.reconnectMaxDelayMs,
    );
    this.emit("reconnecting", { attempt: this.attempt, delayMs });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delayMs);
  }

  /**
   * Sends one already-encrypted relay payload, framed as the client's wire
   * envelope. Returns whether the socket actually accepted the frame, so a
   * caller (see `Portal.broadcastScene`) only treats a payload as delivered
   * once it truly was, instead of assuming success up front.
   */
  sendRelay(payload: string, durability: RelayDurabilityV1): boolean {
    if (!this.isConnected) {
      // Dropped, not queued: the app already resyncs the full scene every
      // `SYNC_FULL_SCENE_INTERVAL_MS`, so a short gap during reconnect is
      // survivable without a message replay buffer.
      return false;
    }
    const envelope: ClientRelayEnvelopeV1 = {
      version: 1,
      kind: "relay",
      durability,
      payload,
    };
    try {
      this.socket!.send(JSON.stringify(envelope));
      return true;
    } catch (error) {
      console.error("Failed to send a room relay frame", error);
      return false;
    }
  }

  /** Closes the connection for good. No further reconnect attempts follow. */
  close(): void {
    this.manuallyClosed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close(1000, "client closed");
    this.socket = null;
  }
}
